// src/components/products/Table/ProductsTableRow.jsx
import React from "react";
import {
    TableRow,
    TableCell,
    IconButton,
    Checkbox,
    Chip,
    Avatar,
    Box,
    Typography,
    Stack,
    Tooltip,
    useTheme
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TourIcon from "@mui/icons-material/Tour";
import { useTranslation } from "react-i18next";

const MAX_VISIBLE_SUPPLIERS = 2; // ✅ عدد الموردين المرئية قبل عرض +N

const ProductsTableRow = ({
    product,
    index,
    isDark,
    page,
    perPage,
    sortBy,
    sortDir,
    totalItems,
    selectedIds,
    onRowCheckboxClick,
    onMenuOpen,
    isArchive = false,
    menuDisabled = false
}) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const serial =
        sortDir === "asc"
            ? (page - 1) * perPage + index + 1
            : (page - 1) * perPage + (totalItems - index);

    const isOdd = index % 2 === 0;
    const isSelected = selectedIds.includes(product?.id);

    // Extract cover image URL
    const imageUrl = product?.cover_image?.url || product?.cover_image?.preview || product?.cover_image || null;

    return (
        <TableRow
            hover
            sx={{
                backgroundColor: isOdd
                    ? isDark
                        ? "rgba(55,65,81,0.45)"
                        : "rgba(249,250,251,0.9)"
                    : "transparent",
            }}
        >
            <TableCell padding="checkbox">
                <Checkbox
                    color="primary"
                    checked={isSelected}
                    onClick={(e) => onRowCheckboxClick(e, product?.id)}
                    inputProps={{ "aria-labelledby": `product-${product?.id}` }}
                />
            </TableCell>

            <TableCell>{serial}</TableCell>

            {/* Cover Image */}
            <TableCell>
                <Avatar
                    src={imageUrl}
                    alt={product?.title?.en || "Product"}
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: isDark ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)",
                    }}
                >
                    {!imageUrl && <TourIcon />}
                </Avatar>
            </TableCell>

            {/* Title Arabic */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                <Box sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                        {product?.title?.ar || "-"}
                    </Typography>
                </Box>
            </TableCell>

            {/* Title English */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                <Box sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                        {product?.title?.en || "-"}
                    </Typography>
                </Box>
            </TableCell>

            {/* Product Type */}
            <TableCell>
                <Chip
                    label={product?.product_type?.name?.[lng] || "-"}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                    }}
                />
            </TableCell>

            {/* Category */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                {product?.category?.name?.[lng] || "-"}
            </TableCell>

            {/* Suppliers - Multiple Chips with Tooltip */}
            <TableCell>
                {product?.suppliers && product.suppliers.length > 0 ? (
                    <Stack direction="row" spacing={0.5} rowGap={1} flexWrap="wrap">
                        {product.suppliers.slice(0, MAX_VISIBLE_SUPPLIERS).map((supplier) => (
                            <Chip
                                key={supplier?.id}
                                label={isArabic ? supplier?.name?.ar : supplier?.name?.en || supplier?.name?.ar}
                                size="small"
                                sx={{ mb: 0.3, maxWidth: 120 }}
                            />
                        ))}
                        {product.suppliers.length > MAX_VISIBLE_SUPPLIERS && (
                            <Tooltip
                                arrow
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: isDark ? '#020617' : '#FFFFFF',
                                            color: isDark ? '#E5E7EB' : '#111827',
                                            boxShadow: theme.shadows[3],
                                            border: `1px solid ${isDark
                                                ? 'rgba(148,163,184,0.4)'
                                                : 'rgba(148,163,184,0.6)'
                                                }`,
                                            px: 1.2,
                                            py: 0.8,
                                            borderRadius: 1.5,
                                            maxWidth: 260,
                                        },
                                    },
                                    arrow: {
                                        sx: {
                                            color: isDark ? '#020617' : '#FFFFFF',
                                        },
                                    },
                                }}
                                title={
                                    <Box>
                                        <Box
                                            sx={{
                                                mb: 0.5,
                                                fontSize: 11,
                                                fontWeight: 600,
                                                opacity: 0.8,
                                            }}
                                        >
                                            {isArabic ? 'جميع الموردين' : 'All suppliers'}
                                        </Box>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {product.suppliers.map((supplier) => (
                                                <Chip
                                                    key={supplier?.id}
                                                    label={
                                                        isArabic
                                                            ? supplier?.name?.ar
                                                            : supplier?.name?.en || supplier?.name?.ar
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        mb: 0.3,
                                                        fontSize: 11,
                                                        height: 22,
                                                        borderColor: isDark
                                                            ? 'rgba(148,163,184,0.6)'
                                                            : 'rgba(59,130,246,0.4)',
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                }
                            >
                                <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={`+${product.suppliers.length - MAX_VISIBLE_SUPPLIERS}`}
                                    sx={{
                                        mb: 0.3,
                                        fontSize: 11,
                                        height: 22,
                                        bgcolor: isDark
                                            ? 'rgba(37,99,235,0.12)'
                                            : 'rgba(37,99,235,0.04)',
                                        borderColor: isDark
                                            ? 'rgba(96,165,250,0.6)'
                                            : 'rgba(37,99,235,0.6)',
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                        -
                    </Typography>
                )}
            </TableCell>

            {/* Transportation */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                {product?.transportation?.name?.[lng] || "-"}
            </TableCell>

            {/* Actions Menu */}
            <TableCell align="right">
                <IconButton
                    size="small"
                    disabled={menuDisabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e, product);
                    }}
                    sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default ProductsTableRow;
